import React, { useMemo, useState, useEffect } from 'react';
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    functionalUpdate,
    SortingState,
    VisibilityState,
} from '@tanstack/react-table';
import { Collaborator } from '@/models';
import type { CollaboratorAllocationSummaryEntry } from '@/features/Collaborators/Models';
import { ALLOCATION_TYPE_METADATA, AllocationBucketTypes } from '@/features/Collaborators/Models';
import { ALLOCATION_STATUS_METADATA, getAllocationStatusRank, getAllocationStatusTitle } from '../constants/allocationStatus';
import UpdateStatusCell, { useStatusSorting } from '@/features/Collaborators/List/Tables/UpdateStatusCell';
import NameCell from '@/features/Collaborators/List/Tables/NameCell';
import { TanStackTableView } from '@/components/shared';
import { calculateConsolidatedStatus, normalizeStatus, translateActivityStatus } from '@/utils/collaborator-status';
import { compareStringsAccentInsensitive, normalizeString } from '@/utils/stringUtils';
import { ActionMenu, ActionMenuItem } from '@/components/shared';
import {
    UserGroupIcon,
    BuildingOfficeIcon,
    CpuChipIcon,
    PencilIcon,
    ClipboardDocumentListIcon,
    LockClosedIcon
} from '@heroicons/react/24/outline';
import { DhoUpdateModal } from '@/features/Collaborators/Edit/Contexts/DhoUpdate';
import { DpUpdateModal } from '@/features/Collaborators/Edit/Contexts/DpUpdate';
import { TechUpdateModal } from '@/features/Collaborators/Edit/Contexts/TechUpdate';
import { useCapabilityCatalogQuery } from '@/features/Collaborators/Data';
import { formatCapacityLabel } from '@/features/Collaborators/Allocations/utils/formatCapacityLabel';
// Não usa mais filterFunctions - dados já vem filtrados
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { formatDateSafe } from '@/utils/dateUtils';
import { useSalaryPermissions } from '@/features/Collaborators/hooks/useSalaryPermissions';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { addEmailToWorkspaceBlacklist, deleteCollaborator } from '@/features/Collaborators/Services';
import ConfirmDialog from '@/features/Projects/pages/ProjectDetails/components/ObjectivesPhase/components/ConfirmDialog';
import {
    buildTechnologyProficiencyEntries,
    resolveCollaboratorResources,
    resolveTechnologyLabel,
    type ResolvedResource,
} from '@/features/Collaborators/utils/resourcePresentation';
import { CapabilityBadge } from '@/features/Collaborators/Shared/CapabilityBadge';
import { buildVacationColumns } from './columns/vacationColumns';

interface CollaboratorStatusManagementTableViewProps {
    collaborators: Collaborator[]; // Dados já filtrados pelo sistema unificado
    isLoading: boolean;
    columnVisibility?: VisibilityState;
}

const columnHelper = createColumnHelper<Collaborator>();

// Comparator usando util global – ignora acentos, caixa e pontuação
const sortAccentInsensitive = (rowA: any, rowB: any, columnId: string) => {
    const a = (rowA.getValue(columnId) as string) || '';
    const b = (rowB.getValue(columnId) as string) || '';
    return compareStringsAccentInsensitive(a, b);
};

// Funções utilitárias para formatação
const formatDate = formatDateSafe;

const formatCPF = (cpf?: string | null): string => {
    if (!cpf) return '';
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11) return cpf;
    return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatWorkSchedule = (hours?: number | null): string => {
    if (!hours) return '';
    return `${hours}h`;
};

const formatRating = (rating?: number | null): string => {
    if (!rating) return '';
    return `${rating}/10`;
};

const toDateSafe = (value: unknown): Date | null => {
    if (!value) return null;
    
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
        const parsed = (value as { toDate: () => Date }).toDate();
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
};

const getAllocationDisplayName = (entry: CollaboratorAllocationSummaryEntry): string => {
    return entry.clientName || 'Sem cliente definido';
};

const getAllocationTypeLabel = (allocationType?: string | null): string => {
    if (!allocationType) return '-';
    return ALLOCATION_TYPE_METADATA[allocationType as keyof typeof ALLOCATION_TYPE_METADATA]?.label ?? allocationType;
};

const CAPACITY_SUFFIX_MAP: Record<string, string> = {
    [AllocationBucketTypes.Defined]: 'horas totais',
    [AllocationBucketTypes.Daily]: 'horas/dia',
    [AllocationBucketTypes.Weekly]: 'horas/semana',
    [AllocationBucketTypes.Monthly]: 'horas/mês',
};

const formatDedication = (bucket?: unknown, bucketType?: string | null): string => {
    if (!bucketType) return 'Sem dedicação';

    const numericBucket = typeof bucket === 'number'
        ? bucket
        : typeof bucket === 'string'
            ? Number.parseFloat(bucket)
            : Number.NaN;

    if (!Number.isFinite(numericBucket)) {
        return 'Sem capacidade';
    }

    const suffix = CAPACITY_SUFFIX_MAP[bucketType] ?? 'horas';
    const value = numericBucket as number;

    const formattedBucket = Number.isInteger(value)
        ? `${value}`
        : value.toLocaleString('pt-BR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
        });

    return `${formattedBucket} ${suffix}`;
};

const formatRate = (rate?: number | null): string => {
    if (typeof rate !== 'number' || Number.isNaN(rate)) {
        return '';
    }
    return rate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const getPeriodLabels = (start?: unknown, end?: unknown) => {
    const startDate = toDateSafe(start);
    const endDate = toDateSafe(end);

    const startLabel = startDate ? startDate.toLocaleDateString('pt-BR') : 'Sem data';
    const hasEnd = Boolean(endDate);
    const endLabel = endDate ? endDate.toLocaleDateString('pt-BR') : 'Sem data de término';

    return { startLabel, hasEnd, endLabel };
};

const getResponsibleLabel = (entry: CollaboratorAllocationSummaryEntry): string => {
    const responsible = entry.responsibleName?.trim();
    const client = entry.clientResponsible?.trim();

    if (responsible && client && responsible !== client) {
        return `${responsible} • ${client}`;
    }

    if (responsible) return responsible;
    if (client) return client;

    return '-';
};

// Funções utilitárias para formatação do Lote 3
const formatCNPJ = (cnpj?: string | null): string => {
    if (!cnpj) return '';
    const cleanCNPJ = cnpj.replace(/\D/g, '');
    if (cleanCNPJ.length !== 14) return cnpj;
    return cleanCNPJ.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatCurrency = (amount?: number | null): string => {
    if (!amount) return '';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(amount);
};

const formatBoolean = (value?: boolean | null): { text: string; icon: string } => {
    if (value === true) return { text: 'Sim', icon: '✅' };
    if (value === false) return { text: 'Não', icon: '❌' };
    return { text: '-', icon: '' };
};

// Funções utilitárias para formatação do Lote 4
const formatPCD = (isPCD?: boolean | null): { text: string; icon: string } => {
    if (isPCD === true) return { text: 'Sim', icon: '♿' };
    if (isPCD === false) return { text: 'Não', icon: '' };
    return { text: '-', icon: '' };
};

const formatRoleType = (roleType?: string | null): string => {
    if (!roleType) return '';
    const translations: Record<string, string> = {
        'administrative': 'Administrativa',
        'operational': 'Operacional'
    };
    return translations[roleType] || roleType;
};

const resolvePrimaryEmail = (collaborator: Collaborator): string => {
    const candidates = [
        collaborator.email,
        (collaborator as any).corporateEmail,
        (collaborator as any).companyEmail,
        (collaborator as any).workEmail,
        (collaborator as any).personalEmail,
    ];
    const found = candidates.find((value) => typeof value === 'string' && value.trim());
    return found ? found.trim().toLowerCase() : '';
};

const CollaboratorStatusManagementTableView: React.FC<CollaboratorStatusManagementTableViewProps> = ({
    collaborators,
    isLoading,
    columnVisibility = {}
}) => {
    // --- Navigation ---
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { success: showSuccess, error: showError } = useToast();

    const capabilityCatalogQuery = useCapabilityCatalogQuery();
    const displayCollaborators = useMemo(
        () => collaborators.map((collaborator) => resolveCollaboratorResources(collaborator, capabilityCatalogQuery.data)),
        [collaborators, capabilityCatalogQuery.data]
    );

    // --- Salary Permissions ---
    const { canViewSalary, shouldShowAsDisabled } = useSalaryPermissions();

    // --- URL state (pagination & sorting) ---
    const [searchParams, setSearchParams] = useSearchParams();

    const initialPageIndex = parseInt(searchParams.get('page') || '0', 10);
    const initialPageSize = parseInt(searchParams.get('size') || '10', 10);

    const initialSorting: SortingState = (() => {
        const sortId = searchParams.get('sort');
        const dir = searchParams.get('dir');
        if (sortId) {
            return [{ id: sortId, desc: dir === 'desc' }];
        }
        return [{ id: 'consolidatedStatus', desc: false }];
    })();

    const [pagination, setPagination] = useState({ pageIndex: initialPageIndex, pageSize: initialPageSize });
    const [sorting, setSorting] = useState<SortingState>(initialSorting);

    // Reset pagination when collaborators change (due to filters)
    useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, [collaborators.length]);

    // --- Status Sorting Hook ---
    const { sortConsolidatedStatus, sortDhoStatus, sortDpStatus, sortTechStatus, sortUserStatus } = useStatusSorting();

    // --- Modal States ---
    const [isDhoModalOpen, setIsDhoModalOpen] = useState(false);
    const [isDpModalOpen, setIsDpModalOpen] = useState(false);
    const [isTechModalOpen, setIsTechModalOpen] = useState(false);
    const [selectedCollaboratorId, setSelectedCollaboratorId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; email: string; name?: string | null } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // --- Column Visibility is controlled by parent component
    // --- Data is already filtered by the unified filter system

    // --- Modal Handlers ---
    const handleOpenDhoUpdateModal = (collaboratorId: string) => {
        setSelectedCollaboratorId(collaboratorId);
        setIsDhoModalOpen(true);
    };

    const handleCloseDhoUpdateModal = () => {
        setIsDhoModalOpen(false);
        setSelectedCollaboratorId(null);
    };

    const handleOpenDpUpdateModal = (collaboratorId: string) => {
        setSelectedCollaboratorId(collaboratorId);
        setIsDpModalOpen(true);
    };

    const handleCloseDpUpdateModal = () => {
        setIsDpModalOpen(false);
        setSelectedCollaboratorId(null);
    };

    const handleOpenTechUpdateModal = (collaboratorId: string) => {
        setSelectedCollaboratorId(collaboratorId);
        setIsTechModalOpen(true);
    };

    const handleCloseTechUpdateModal = () => {
        setIsTechModalOpen(false);
        setSelectedCollaboratorId(null);
    };

    const handleEditClick = (collaboratorId: string) => {
        // Pass current URL with search params to preserve state
        const currentUrl = `${location.pathname}${location.search}`;
        navigate(`/collaborators/${collaboratorId}/edit`, {
            state: { from: currentUrl }
        });
    };
    const handleNavigateToAllocations = (collaboratorId: string) => {
        navigate(`/collaborators/${collaboratorId}/allocations`);
    };

    const handleEditAllocation = (collaboratorId: string, allocationId: string) => {
        navigate(`/collaborators/${collaboratorId}/allocations/${allocationId}/edit`);
    };

    const handleRequestDeleteAndBlacklist = (collaborator: Collaborator) => {
        if (!collaborator.id) {
            showError('Não foi possível identificar o colaborador para exclusão.');
            return;
        }
        setDeleteTarget({
            id: collaborator.id,
            email: resolvePrimaryEmail(collaborator),
            name: collaborator.name,
        });
    };

    const handleConfirmDeleteAndBlacklist = async () => {
        if (isDeleting) return;
        if (!deleteTarget) return;
        if (!user) {
            showError('É necessário estar autenticado para excluir uma conta.');
            return;
        }
        const normalizedEmail = deleteTarget.email?.trim().toLowerCase();
        if (!normalizedEmail) {
            showError('Não foi possível bloquear: colaborador sem e-mail válido.');
            setDeleteTarget(null);
            return;
        }
        setIsDeleting(true);
        try {
            await addEmailToWorkspaceBlacklist(normalizedEmail, user, 'Remoção manual via painel de atualizações');
            await deleteCollaborator(deleteTarget.id);
            showSuccess('Conta excluída e e-mail colocado na blacklist.');
            setDeleteTarget(null);
        } catch (error) {
            console.error('[CollaboratorStatusManagementTableView] failed to delete and blacklist collaborator', error);
            showError('Não foi possível concluir a exclusão e o bloqueio do e-mail.');
        } finally {
            setIsDeleting(false);
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'NOME',
            enableHiding: false, // Coluna essencial - não pode ser ocultada
            cell: ({ row }) => (
                <NameCell
                    name={row.original.name}
                    subtitle={row.original.positionName}
                    avatarUrl={row.original.photoUrl}
                    activityStatus={translateActivityStatus(row.original.status)}
                    isRaroAcademyAlumnus={row.original.isRaroAcademyAlumnus}
                    linkTo={`/collaborators/${row.original.id}`}
                />
            ),
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados // Usar o novo sistema de filtros
        }),
        
        // Coluna virtual para filtros de cards
        columnHelper.display({
            id: 'cardFilter',
            header: '',
            cell: () => null, // Coluna invisível
            enableHiding: false, // Sempre presente para filtros
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('status', {
            id: 'activityStatus',
            header: 'ATIVIDADE',
            size: 120,
            cell: ({ row }) => {
                const status = row.original.status;
                const translatedStatus = translateActivityStatus(status);
                const isActive = status?.toLowerCase() === 'active';

                return (
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span className={`text-sm font-medium ${isActive ? 'text-green-700' : 'text-gray-600'}`}>
                            {translatedStatus}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB, columnId) => {
                const aStatus = (rowA.getValue(columnId) as string)?.toLowerCase() || '';
                const bStatus = (rowB.getValue(columnId) as string)?.toLowerCase() || '';
                // Ativo (active) vem primeiro
                if (aStatus === 'active' && bStatus !== 'active') return -1;
                if (bStatus === 'active' && aStatus !== 'active') return 1;
                return aStatus.localeCompare(bStatus);
            },
            // Não precisa de filterFn - dados já vêm filtrados // Adicionar filtro para status
        }),
        columnHelper.accessor((row) => calculateConsolidatedStatus(row), {
            id: 'consolidatedStatus',
            header: 'STATUS',
            cell: ({ getValue }) => <UpdateStatusCell status={getValue()} />,
            enableSorting: true,
            sortingFn: sortConsolidatedStatus,
        }),
        columnHelper.accessor(
            (row) => normalizeStatus(row.updatedStatus?.byDHO?.status),
            {
                id: 'dhoStatus',
                header: 'STATUS DHO',
                cell: (info) => <UpdateStatusCell status={info.getValue()} />,
                enableSorting: true,
                sortingFn: sortDhoStatus,
            },
        ),
        columnHelper.accessor(
            (row) => normalizeStatus(row.updatedStatus?.byDP?.status),
            {
                id: 'dpStatus',
                header: 'STATUS DP',
                cell: (info) => <UpdateStatusCell status={info.getValue()} />,
                enableSorting: true,
                sortingFn: sortDpStatus,
            },
        ),
        columnHelper.accessor(
            (row) => normalizeStatus(row.updatedStatus?.byTech?.status),
            {
                id: 'techStatus',
                header: 'STATUS TECH',
                cell: (info) => <UpdateStatusCell status={info.getValue()} />,
                enableSorting: true,
                sortingFn: sortTechStatus,
            },
        ),
        columnHelper.accessor(
            (row) => normalizeStatus(row.updatedStatus?.byUser?.status),
            {
                id: 'userStatus',
                header: 'STATUS USER',
                cell: (info) => <UpdateStatusCell status={info.getValue()} />,
                enableSorting: true,
                sortingFn: sortUserStatus,
            },
        ),

        columnHelper.display({
            id: 'allocationStatus',
            header: 'STATUS DE ALOCAÇÃO',
            cell: ({ row }) => {
                const preview = row.original.allocationPreview;
                const status = preview?.status ?? null;
                const collaboratorId = row.original.id;

                if (!status) {
                    return (
                        <button
                            type="button"
                            className="text-xs text-gray-400 hover:text-gray-600 transition"
                            onClick={() => collaboratorId && handleNavigateToAllocations(collaboratorId)}
                            disabled={!collaboratorId}
                        >
                            Sem dados
                        </button>
                    );
                }

                const metadata = ALLOCATION_STATUS_METADATA[status];
                const label = preview?.label || metadata?.title || getAllocationStatusTitle(status);
                const badgeClass = metadata?.badgeClass ?? 'bg-gray-100 text-gray-600 border border-gray-200';
                const icon = metadata?.icon ?? '📊';

                return (
                    <button
                        type="button"
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium uppercase tracking-wide transition ${badgeClass} hover:opacity-90`}
                        onClick={() => collaboratorId && handleNavigateToAllocations(collaboratorId)}
                        disabled={!collaboratorId}
                    >
                        <span>{icon}</span>
                        <span>{label}</span>
                    </button>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const statusA = rowA.original.allocationPreview?.status;
                const statusB = rowB.original.allocationPreview?.status;
                return getAllocationStatusRank(statusA) - getAllocationStatusRank(statusB);
            },
        }),

        columnHelper.display({
            id: 'allocationSummary',
            header: 'ALOCACOES ATIVAS',
            cell: ({ row }) => {
                const summary = row.original.allocationSummary?.active ?? [];

                if (!summary.length) {
                    return <span className="text-xs text-gray-400">Sem alocações ativas</span>;
                }

                return (
                    <div className="overflow-hidden rounded-md border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    <th className="px-3 py-2 text-left">Tipo</th>
                                    <th className="px-3 py-2 text-left">Cliente</th>
                                    <th className="px-3 py-2 text-left">Período</th>
                                    <th className="px-3 py-2 text-left">Dedicação</th>
                                    <th className="px-3 py-2 text-left">Responsáveis</th>
                                    <th className="px-2 py-2 text-right">
                                        <span className="sr-only">Ações</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {summary.map(entry => {
                                    const collaboratorId = row.original.id;
                                    const type = getAllocationTypeLabel(entry.allocationType);
                                    const client = getAllocationDisplayName(entry);
                                    const { startLabel, hasEnd, endLabel } = getPeriodLabels(entry.startDate, entry.endDate);
                                    const dedication = formatDedication(entry.bucket, entry.bucketType);
                                    const rate = formatRate(entry.rate);
                                    const responsible = getResponsibleLabel(entry);
                                    const capacity = formatCapacityLabel(entry);

                                    return (
                                        <tr key={entry.id} className="text-xs text-gray-700">
                                            <td className="px-3 py-2 align-top font-medium text-gray-900">
                                                <div>{type}</div>
                                                {capacity && (
                                                    <div className="text-[11px] font-normal text-gray-500">
                                                        {capacity}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div>{client}</div>
                                                {entry.projectName && entry.projectName !== entry.clientName && (
                                                    <div className="text-[11px] text-gray-500">{entry.projectName}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div>{startLabel}</div>
                                                <div className="text-[11px] text-gray-500">
                                                    {hasEnd ? `até ${endLabel}` : endLabel}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div>{dedication}</div>
                                                {rate && (
                                                    <div className="text-[11px] text-gray-500">{rate}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 align-top">{responsible}</td>
                                            <td className="px-2 py-2 align-top text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-xs p-0 h-8 w-8 flex items-center justify-center rounded-full"
                                                    onClick={() => collaboratorId && handleEditAllocation(collaboratorId, entry.id)}
                                                    disabled={!collaboratorId}
                                                >
                                                    <PencilIcon className="h-4 w-4" aria-hidden="true" />
                                                    <span className="sr-only">Editar alocação</span>
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            },
            enableSorting: false,
        }),
        
        // --- Dados de Contato ---
        columnHelper.accessor('email', {
            id: 'email',
            header: 'EMAIL',
            cell: ({ getValue }) => {
                const email = getValue();
                return (
                    <div className="text-sm">
                        {email ? (
                            <a 
                                href={`mailto:${email}`} 
                                className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                                {email}
                            </a>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('phone', {
            id: 'phone',
            header: 'TELEFONE',
            cell: ({ getValue }) => {
                const phone = getValue();
                
                // Função para formatar telefone para WhatsApp (apenas números)
                const formatPhoneForWhatsApp = (phoneNumber: string) => {
                    return phoneNumber.replace(/\D/g, '');
                };
                
                return (
                    <div className="text-sm">
                        {phone ? (
                            <a 
                                href={`https://wa.me/55${formatPhoneForWhatsApp(phone)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800 hover:underline flex items-center gap-1"
                            >
                                💬 {phone}
                            </a>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        // --- Dados Profissionais ---
        columnHelper.accessor('positionName', {
            id: 'positionName',
            header: 'CARGO',
            cell: ({ getValue }) => {
                const position = getValue();
                return (
                    <div className="text-sm">
                        {position ? (
                            <span className="text-gray-700 font-medium">{position}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('departmentName', {
            id: 'departmentName',
            header: 'DEPARTAMENTO',
            cell: ({ getValue }) => {
                const department = getValue();
                return (
                    <div className="text-sm">
                        {department ? (
                            <span className="text-gray-700">{department}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Localização ---
        columnHelper.accessor((row) => row.addresses?.[0]?.city, {
            id: 'addressCity',
            header: 'CIDADE',
            cell: ({ getValue }) => {
                const city = getValue();
                return (
                    <div className="text-sm">
                        {city ? (
                            <span className="text-gray-700">{city}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            enableHiding: true, // Permitir ocultar mas manter filtro ativo
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor((row) => row.addresses?.[0]?.state, {
            id: 'addressState',
            header: 'ESTADO',
            cell: ({ getValue }) => {
                const state = getValue();
                return (
                    <div className="text-sm">
                        {state ? (
                            <span className="text-gray-700">{state}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Dados Pessoais (Lote 2) ---
        columnHelper.accessor('birthDate', {
            id: 'birthDate',
            header: 'DATA DE NASCIMENTO',
            cell: ({ getValue }) => {
                const birthDate = getValue();
                const formattedDate = formatDate(birthDate);
                return (
                    <div className="text-sm">
                        {formattedDate ? (
                            <span className="text-gray-700">{formattedDate}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('cpf', {
            id: 'cpf',
            header: 'CPF',
            cell: ({ getValue }) => {
                const cpf = getValue();
                const formattedCPF = formatCPF(cpf);
                return (
                    <div className="text-sm">
                        {formattedCPF ? (
                            <span className="text-gray-700 font-mono">{formattedCPF}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('sex', {
            id: 'sex',
            header: 'SEXO',
            cell: ({ getValue }) => {
                const sex = getValue();
                return (
                    <div className="text-sm">
                        {sex ? (
                            <span className="text-gray-700">{sex}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Dados Contratuais (Lote 2) ---
        columnHelper.accessor('collaboratorType', {
            id: 'collaboratorType',
            header: 'TIPO DE COLABORADOR',
            cell: ({ getValue }) => {
                const type = getValue();
                return (
                    <div className="text-sm">
                        {type ? (
                            <span className="text-gray-700 font-medium">{type}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('startDate', {
            id: 'startDate',
            header: 'DATA DE INÍCIO',
            cell: ({ getValue }) => {
                const startDate = getValue();
                const formattedDate = formatDate(startDate);
                return (
                    <div className="text-sm">
                        {formattedDate ? (
                            <span className="text-gray-700">{formattedDate}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('workSchedule', {
            id: 'workSchedule',
            header: 'CARGA HORÁRIA',
            cell: ({ getValue }) => {
                const workSchedule = getValue();
                const formattedSchedule = formatWorkSchedule(workSchedule);
                return (
                    <div className="text-sm">
                        {formattedSchedule ? (
                            <span className="text-gray-700">{formattedSchedule}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        // --- Dados Técnicos (Lote 2) ---
        columnHelper.accessor('seniorityName', {
            id: 'seniorityName',
            header: 'SENIORIDADE',
            cell: ({ getValue }) => {
                const seniority = getValue();
                return (
                    <div className="text-sm">
                        {seniority ? (
                            <span className="text-gray-700 font-medium">{seniority}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('rating', {
            id: 'rating',
            header: 'RATING',
            cell: ({ getValue }) => {
                const rating = getValue();
                const formattedRating = formatRating(rating);
                return (
                    <div className="text-sm">
                        {rating ? (
                            <div className="flex items-center gap-2">
                                <span className="text-gray-700 font-medium">{formattedRating}</span>
                                <div className="flex">
                                    {Array.from({ length: 5 }, (_, i) => {
                                        const filled = i < Math.floor(rating / 2);
                                        return (
                                            <svg
                                                key={i}
                                                className={`w-3 h-3 ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        // --- Responsabilidades (Lote 2) ---
        columnHelper.accessor('responsibleName', {
            id: 'responsibleName',
            header: 'RESPONSÁVEL',
            cell: ({ getValue }) => {
                const responsible = getValue();
                return (
                    <div className="text-sm">
                        {responsible ? (
                            <span className="text-gray-700 font-medium">{responsible}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('clientResponsible', {
            id: 'clientResponsible',
            header: 'CLIENTE RESPONSÁVEL',
            cell: ({ getValue }) => {
                const clientResponsible = getValue();
                return (
                    <div className="text-sm">
                        {clientResponsible ? (
                            <span className="text-gray-700">{clientResponsible}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Benefícios (Lote 3) ---
        columnHelper.accessor('hasHealthInsurance', {
            id: 'hasHealthInsurance',
            header: 'PLANO DE SAÚDE',
            cell: ({ getValue }) => {
                const hasInsurance = getValue();
                const formatted = formatBoolean(hasInsurance);
                return (
                    <div className="text-sm">
                        <div className="flex items-center gap-2">
                            {formatted.icon && <span>{formatted.icon}</span>}
                            <span className={hasInsurance ? 'text-green-700' : hasInsurance === false ? 'text-red-600' : 'text-gray-400'}>
                                {formatted.text}
                            </span>
                        </div>
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('hasCajuBenefit', {
            id: 'hasCajuBenefit',
            header: 'BENEFÍCIO CAJU',
            cell: ({ getValue }) => {
                const hasBenefit = getValue();
                const formatted = formatBoolean(hasBenefit);
                return (
                    <div className="text-sm">
                        <div className="flex items-center gap-2">
                            {formatted.icon && <span>{formatted.icon}</span>}
                            <span className={hasBenefit ? 'text-green-700' : hasBenefit === false ? 'text-red-600' : 'text-gray-400'}>
                                {formatted.text}
                            </span>
                        </div>
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('hasWellhubBenefit', {
            id: 'hasWellhubBenefit',
            header: 'BENEFÍCIO WELLHUB',
            cell: ({ getValue }) => {
                const hasBenefit = getValue();
                const formatted = formatBoolean(hasBenefit);
                return (
                    <div className="text-sm">
                        <div className="flex items-center gap-2">
                            {formatted.icon && <span>{formatted.icon}</span>}
                            <span className={hasBenefit ? 'text-green-700' : hasBenefit === false ? 'text-red-600' : 'text-gray-400'}>
                                {formatted.text}
                            </span>
                        </div>
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('transportationBenefitAmount', {
            id: 'transportationBenefitAmount',
            header: 'VALE TRANSPORTE',
            cell: ({ getValue }) => {
                const amount = getValue();
                const formattedAmount = formatCurrency(amount);
                return (
                    <div className="text-sm">
                        {formattedAmount ? (
                            <span className="text-gray-700 font-medium">{formattedAmount}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        // --- Dados Específicos (Lote 3) ---
        columnHelper.accessor('cnpj', {
            id: 'cnpj',
            header: 'CNPJ',
            cell: ({ getValue }) => {
                const cnpj = getValue();
                const formattedCNPJ = formatCNPJ(cnpj);
                return (
                    <div className="text-sm">
                        {formattedCNPJ ? (
                            <span className="text-gray-700 font-mono">{formattedCNPJ}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('companyName', {
            id: 'companyName',
            header: 'NOME DA EMPRESA',
            cell: ({ getValue }) => {
                const companyName = getValue();
                return (
                    <div className="text-sm">
                        {companyName ? (
                            <span className="text-gray-700 font-medium">{companyName}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('isRaroAcademyAlumnus', {
            id: 'isRaroAcademyAlumnus',
            header: 'EX-RARO ACADEMY',
            cell: ({ getValue }) => {
                const isAlumnus = getValue();
                const formatted = formatBoolean(isAlumnus);
                return (
                    <div className="text-sm">
                        <div className="flex items-center gap-2">
                            {formatted.icon && <span>{formatted.icon}</span>}
                            <span className={isAlumnus ? 'text-purple-700 font-medium' : isAlumnus === false ? 'text-gray-600' : 'text-gray-400'}>
                                {formatted.text}
                            </span>
                        </div>
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        // --- Informações Familiares (Lote 3) ---
        columnHelper.accessor('maritalStatus', {
            id: 'maritalStatus',
            header: 'ESTADO CIVIL',
            cell: ({ getValue }) => {
                const maritalStatus = getValue();
                return (
                    <div className="text-sm">
                        {maritalStatus ? (
                            <span className="text-gray-700">{maritalStatus}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('motherName', {
            id: 'motherName',
            header: 'NOME DA MÃE',
            cell: ({ getValue }) => {
                const motherName = getValue();
                return (
                    <div className="text-sm">
                        {motherName ? (
                            <span className="text-gray-700">{motherName}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Contato Adicional (Lote 3) ---
        columnHelper.accessor('personalEmail', {
            id: 'personalEmail',
            header: 'EMAIL PESSOAL',
            cell: ({ getValue }) => {
                const personalEmail = getValue();
                return (
                    <div className="text-sm">
                        {personalEmail ? (
                            <a 
                                href={`mailto:${personalEmail}`} 
                                className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                                {personalEmail}
                            </a>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Dados de Estagiário (Lote 4) ---
        columnHelper.accessor('internshipEducationalInstitution', {
            id: 'internshipEducationalInstitution',
            header: 'INSTITUIÇÃO DE ENSINO',
            cell: ({ getValue }) => {
                const institution = getValue();
                return (
                    <div className="text-sm">
                        {institution ? (
                            <span className="text-gray-700">{institution}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('internshipCourse', {
            id: 'internshipCourse',
            header: 'CURSO',
            cell: ({ getValue }) => {
                const course = getValue();
                return (
                    <div className="text-sm">
                        {course ? (
                            <span className="text-gray-700 font-medium">{course}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('internshipEndDate', {
            id: 'internshipEndDate',
            header: 'DATA DE TÉRMINO DO ESTÁGIO',
            cell: ({ getValue }) => {
                const endDate = getValue();
                const formattedDate = formatDate(endDate);
                return (
                    <div className="text-sm">
                        {formattedDate ? (
                            <span className="text-gray-700">{formattedDate}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        // --- Informações de Saúde (Lote 4) ---
        columnHelper.accessor('healthInsuranceProvider', {
            id: 'healthInsuranceProvider',
            header: 'OPERADORA DO PLANO',
            cell: ({ getValue }) => {
                const provider = getValue();
                return (
                    <div className="text-sm">
                        {provider ? (
                            <span className="text-gray-700 font-medium">{provider}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('isPCD', {
            id: 'isPCD',
            header: 'É PCD',
            cell: ({ getValue }) => {
                const isPCD = getValue();
                const formatted = formatPCD(isPCD);
                return (
                    <div className="text-sm">
                        <div className="flex items-center gap-2">
                            {formatted.icon && <span className="text-lg">{formatted.icon}</span>}
                            <span className={isPCD ? 'text-blue-700 font-medium' : isPCD === false ? 'text-gray-600' : 'text-gray-400'}>
                                {formatted.text}
                            </span>
                        </div>
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('esocialCode', {
            id: 'esocialCode',
            header: 'CÓDIGO ESOCIAL',
            cell: ({ getValue }) => {
                const code = getValue();
                return (
                    <div className="text-sm">
                        {code ? (
                            <span className="text-gray-700 font-mono">{code}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Dados Pessoais Estendidos (Lote 4) ---
        columnHelper.accessor('nationality', {
            id: 'nationality',
            header: 'NACIONALIDADE',
            cell: ({ getValue }) => {
                const nationality = getValue();
                return (
                    <div className="text-sm">
                        {nationality ? (
                            <span className="text-gray-700">{nationality}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('race', {
            id: 'race',
            header: 'RAÇA/COR',
            cell: ({ getValue }) => {
                const race = getValue();
                return (
                    <div className="text-sm">
                        {race ? (
                            <span className="text-gray-700">{race}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // --- Dados Contratuais Estendidos (Lote 4) ---
        columnHelper.accessor('endDate', {
            id: 'endDate',
            header: 'DATA DE FIM',
            cell: ({ getValue }) => {
                const endDate = getValue();
                const formattedDate = formatDate(endDate);
                return (
                    <div className="text-sm">
                        {formattedDate ? (
                            <span className="text-gray-700">{formattedDate}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('roleType', {
            id: 'roleType',
            header: 'TIPO DE FUNÇÃO',
            cell: ({ getValue }) => {
                const roleType = getValue();
                const formattedRole = formatRoleType(roleType);
                return (
                    <div className="text-sm">
                        {formattedRole ? (
                            <span className="text-gray-700 font-medium">{formattedRole}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // === LOTE 5 - Benefícios Adicionais, Informações Familiares Estendidas, Preferências Pessoais, Dados Contratuais Finais ===
        
        columnHelper.accessor('hasZeloBenefit', {
            id: 'hasZeloBenefit',
            header: 'BENEFÍCIO ZELO',
            cell: ({ getValue }) => {
                const value = getValue();
                return (
                    <div className="text-sm">
                        {value ? (
                            <span className="text-green-600 font-medium">✓ Sim</span>
                        ) : value === false ? (
                            <span className="text-gray-500">✗ Não</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('hasGuapecoBenefit', {
            id: 'hasGuapecoBenefit',
            header: 'BENEFÍCIO GUAPECO',
            cell: ({ getValue }) => {
                const value = getValue();
                return (
                    <div className="text-sm">
                        {value ? (
                            <span className="text-green-600 font-medium">✓ Sim</span>
                        ) : value === false ? (
                            <span className="text-gray-500">✗ Não</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('healthInsuranceInclusionDate', {
            id: 'healthInsuranceInclusionDate',
            header: 'DATA DE INCLUSÃO NO PLANO',
            cell: ({ getValue }) => {
                const date = getValue();
                return (
                    <div className="text-sm">
                        {date ? (
                            <span className="text-gray-700">{formatDate(date)}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('fatherName', {
            id: 'fatherName',
            header: 'NOME DO PAI',
            cell: ({ getValue }) => {
                const name = getValue();
                return (
                    <div className="text-sm max-w-[200px] truncate">
                        {name ? (
                            <span className="text-gray-700" title={name}>{name}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('spouseName', {
            id: 'spouseName',
            header: 'NOME DO CÔNJUGE',
            cell: ({ getValue }) => {
                const name = getValue();
                return (
                    <div className="text-sm max-w-[200px] truncate">
                        {name ? (
                            <span className="text-gray-700" title={name}>{name}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('gender', {
            id: 'gender',
            header: 'GÊNERO',
            cell: ({ getValue }) => {
                const gender = getValue();
                return (
                    <div className="text-sm">
                        {gender ? (
                            <span className="text-gray-700 font-medium">{gender}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('shirtSize', {
            id: 'shirtSize',
            header: 'TAMANHO DA CAMISA',
            cell: ({ getValue }) => {
                const size = getValue();
                return (
                    <div className="text-sm">
                        {size ? (
                            <span className="text-gray-700 font-medium bg-gray-100 px-2 py-1 rounded text-xs">{size}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.accessor('hasOnboardingKit', {
            id: 'hasOnboardingKit',
            header: 'KIT DE ONBOARDING',
            cell: ({ getValue }) => {
                const value = getValue();
                return (
                    <div className="text-sm">
                        {value ? (
                            <span className="text-green-600 font-medium">📦 Sim</span>
                        ) : value === false ? (
                            <span className="text-gray-500">📦 Não</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('experienceEndDate', {
            id: 'experienceEndDate',
            header: 'DATA DE FIM DA EXPERIÊNCIA',
            cell: ({ getValue }) => {
                const date = getValue();
                return (
                    <div className="text-sm">
                        {date ? (
                            <span className="text-gray-700">{formatDate(date)}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
        }),
        
        columnHelper.accessor('naturality', {
            id: 'naturality',
            header: 'NATURALIDADE',
            cell: ({ getValue }) => {
                const naturality = getValue();
                return (
                    <div className="text-sm max-w-[150px] truncate">
                        {naturality ? (
                            <span className="text-gray-700" title={naturality}>{naturality}</span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        // === LOTE 6 - Contadores de Relacionamentos 1-para-N ===
        
        columnHelper.display({
            id: 'raroFeedbacksCount',
            header: 'FEEDBACKS INTERNOS',
            cell: ({ row }) => {
                const feedbacks = row.original.raroFeedbacks;
                const count = feedbacks?.length || 0;
                return (
                    <div className="text-sm">
                        {count > 0 ? (
                            <span className="text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded text-xs">
                                💬 {count}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-xs">0</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.raroFeedbacks?.length || 0;
                const countB = rowB.original.raroFeedbacks?.length || 0;
                return countA - countB;
            },
        }),
        
        columnHelper.display({
            id: 'clientFeedbacksCount',
            header: 'FEEDBACKS DE CLIENTE',
            cell: ({ row }) => {
                const feedbacks = row.original.clientFeedbacks;
                const count = feedbacks?.length || 0;
                return (
                    <div className="text-sm">
                        {count > 0 ? (
                            <span className="text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded text-xs">
                                🗣️ {count}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-xs">0</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.clientFeedbacks?.length || 0;
                const countB = rowB.original.clientFeedbacks?.length || 0;
                return countA - countB;
            },
        }),
        
        columnHelper.display({
            id: 'occurrencesCount',
            header: 'OCORRÊNCIAS',
            cell: ({ row }) => {
                const occurrences = row.original.occurrences;
                const count = occurrences?.length || 0;
                return (
                    <div className="text-sm">
                        {count > 0 ? (
                            <span className="text-red-600 font-medium bg-red-50 px-2 py-1 rounded text-xs">
                                ⚠️ {count}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-xs">0</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.occurrences?.length || 0;
                const countB = rowB.original.occurrences?.length || 0;
                return countA - countB;
            },
        }),
        
        columnHelper.display({
            id: 'childrenCount',
            header: 'FILHOS',
            cell: ({ row }) => {
                const children = row.original.children;
                const count = children?.length || 0;
                return (
                    <div className="text-sm">
                        {count > 0 ? (
                            <span className="text-green-600 font-medium bg-green-50 px-2 py-1 rounded text-xs">
                                👶 {count}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-xs">0</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.children?.length || 0;
                const countB = rowB.original.children?.length || 0;
                return countA - countB;
            },
        }),
        
        columnHelper.display({
            id: 'healthDependentsCount',
            header: 'DEPENDENTES DO PLANO',
            cell: ({ row }) => {
                const dependents = row.original.healthDependents;
                const count = dependents?.length || 0;
                return (
                    <div className="text-sm">
                        {count > 0 ? (
                            <span className="text-teal-600 font-medium bg-teal-50 px-2 py-1 rounded text-xs">
                                🏥 {count}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-xs">0</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.healthDependents?.length || 0;
                const countB = rowB.original.healthDependents?.length || 0;
                return countA - countB;
            },
        }),
        
        // === LOTE 7 - Endereços Específicos (Campos Derivados) ===
        
        columnHelper.display({
            id: 'mainAddress',
            header: 'ENDEREÇO PRINCIPAL',
            cell: ({ row }) => {
                const addresses = row.original.addresses;
                const mainAddress = addresses?.find(addr => addr.type === 'main');
                
                // Função para formatar endereço completo
                const formatAddress = (addr: any) => {
                    if (!addr) return null;
                    
                    const parts = [
                        addr.street && addr.number ? `${addr.street}, ${addr.number}` : addr.street || addr.number,
                        addr.complement,
                        addr.neighborhood,
                        addr.city && addr.state ? `${addr.city}/${addr.state}` : addr.city || addr.state
                    ].filter(Boolean);
                    
                    return parts.length > 0 ? parts.join(' - ') : null;
                };
                
                const formattedAddress = formatAddress(mainAddress);
                
                return (
                    <div className="text-sm min-w-0 max-w-[300px] overflow-hidden">
                        {formattedAddress ? (
                            <div className="text-gray-700 break-words whitespace-normal">
                                🏠 {formattedAddress}
                            </div>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getMainAddressCity = (addresses: any[]) => {
                    const mainAddr = addresses?.find(addr => addr.type === 'main');
                    return mainAddr?.city || '';
                };
                
                const cityA = getMainAddressCity(rowA.original.addresses);
                const cityB = getMainAddressCity(rowB.original.addresses);
                return cityA.localeCompare(cityB, 'pt-BR', { sensitivity: 'base' });
            },
        }),
        
        columnHelper.display({
            id: 'mailingAddress',
            header: 'ENDEREÇO DE CORRESPONDÊNCIA',
            cell: ({ row }) => {
                const addresses = row.original.addresses;
                const mailingAddress = addresses?.find(addr => addr.type === 'mailing');
                
                // Função para formatar endereço completo
                const formatAddress = (addr: any) => {
                    if (!addr) return null;
                    
                    const parts = [
                        addr.street && addr.number ? `${addr.street}, ${addr.number}` : addr.street || addr.number,
                        addr.complement,
                        addr.neighborhood,
                        addr.city && addr.state ? `${addr.city}/${addr.state}` : addr.city || addr.state
                    ].filter(Boolean);
                    
                    return parts.length > 0 ? parts.join(' - ') : null;
                };
                
                const formattedAddress = formatAddress(mailingAddress);
                
                return (
                    <div className="text-sm min-w-0 max-w-[300px] overflow-hidden">
                        {formattedAddress ? (
                            <div className="text-gray-700 break-words whitespace-normal">
                                📬 {formattedAddress}
                            </div>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getMailingAddressCity = (addresses: any[]) => {
                    const mailingAddr = addresses?.find(addr => addr.type === 'mailing');
                    return mailingAddr?.city || '';
                };
                
                const cityA = getMailingAddressCity(rowA.original.addresses);
                const cityB = getMailingAddressCity(rowB.original.addresses);
                return cityA.localeCompare(cityB, 'pt-BR', { sensitivity: 'base' });
            },
        }),
        
        // === LOTE 8 - Métricas Salariais (Campos Derivados Complexos) ===

        columnHelper.display({
            id: 'lastSalary',
            header: 'ÚLTIMO SALÁRIO',
            cell: ({ row }) => {
                // Check permission first
                if (!canViewSalary) {
                    return (
                        <div className="text-sm flex items-center gap-1">
                            <LockClosedIcon className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-400">Restrito</span>
                        </div>
                    );
                }

                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                // Filtrar apenas salários até hoje (excluir futuros)
                const now = new Date();
                const pastSalaries = salaries?.filter(salary =>
                    new Date(salary.date) <= now
                ) || [];

                if (!pastSalaries.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                // Ordenar por data (mais recente primeiro)
                const sortedSalaries = [...pastSalaries].sort((a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];

                return (
                    <div className="text-sm">
                        <span className="text-gray-700 font-medium">
                            {formatCurrency(lastSalary.amount)}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getLastSalary = (salaries: any[]) => {
                    if (!salaries?.length) return 0;
                    // Filtrar apenas salários até hoje
                    const now = new Date();
                    const pastSalaries = salaries.filter(salary => 
                        new Date(salary.date) <= now
                    );
                    if (!pastSalaries.length) return 0;
                    const sorted = [...pastSalaries].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    return sorted[0].amount || 0;
                };
                
                return getLastSalary(rowA.original.salaryHistorySalaries) - 
                       getLastSalary(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'salaryProgression',
            header: 'PROGRESSÃO SALARIAL',
            cell: ({ row }) => {
                // Check permission first
                if (!canViewSalary) {
                    return (
                        <div className="text-sm flex items-center gap-1">
                            <LockClosedIcon className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-400">Restrito</span>
                        </div>
                    );
                }

                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length || salaries.length < 2) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                const sortedSalaries = [...salaries].sort((a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                const firstSalary = sortedSalaries[0].amount || 0;
                const lastSalary = sortedSalaries[sortedSalaries.length - 1].amount || 0;

                const progression = lastSalary - firstSalary;
                const isPositive = progression > 0;

                return (
                    <div className="text-sm">
                        <span className={`font-medium ${isPositive ? 'text-green-600' : progression < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {isPositive ? '↗️' : progression < 0 ? '↘️' : '➡️'} {formatCurrency(Math.abs(progression))}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getProgression = (salaries: any[]) => {
                    if (!salaries?.length || salaries.length < 2) return 0;
                    const sorted = [...salaries].sort((a, b) => 
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    return (sorted[sorted.length - 1].amount || 0) - (sorted[0].amount || 0);
                };
                
                return getProgression(rowA.original.salaryHistorySalaries) - 
                       getProgression(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'lastSalaryWithIncrease',
            header: 'ÚLTIMO SALÁRIO + % AUMENTO',
            cell: ({ row }) => {
                // Check permission first
                if (!canViewSalary) {
                    return (
                        <div className="text-sm flex items-center gap-1">
                            <LockClosedIcon className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-400">Restrito</span>
                        </div>
                    );
                }

                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                // Filtrar apenas salários até hoje (excluir futuros)
                const now = new Date();
                const pastSalaries = salaries?.filter(salary =>
                    new Date(salary.date) <= now
                ) || [];

                if (!pastSalaries.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                // Ordenar por data (mais recente primeiro)
                const sortedSalaries = [...pastSalaries].sort((a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];

                // Calcular percentual de aumento se houver salário anterior
                let percentage = null;
                if (sortedSalaries.length >= 2) {
                    const previousSalary = sortedSalaries[1];
                    if (previousSalary.amount > 0) {
                        percentage = ((lastSalary.amount - previousSalary.amount) / previousSalary.amount) * 100;
                    }
                }

                return (
                    <div className="text-sm space-y-1">
                        <div className="text-gray-700 font-medium">
                            {formatCurrency(lastSalary.amount)}
                        </div>
                        {percentage !== null && (
                            <div className={`text-xs font-medium ${percentage > 0 ? 'text-green-600' : percentage < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {percentage > 0 ? '+' : ''}{percentage.toFixed(1)}%
                            </div>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getLastSalary = (salaries: any[]) => {
                    if (!salaries?.length) return 0;
                    // Filtrar apenas salários até hoje
                    const now = new Date();
                    const pastSalaries = salaries.filter(salary => 
                        new Date(salary.date) <= now
                    );
                    if (!pastSalaries.length) return 0;
                    const sorted = [...pastSalaries].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    return sorted[0].amount || 0;
                };
                
                return getLastSalary(rowA.original.salaryHistorySalaries) - 
                       getLastSalary(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'timeSinceLastRaise',
            header: 'TEMPO SEM AUMENTO',
            cell: ({ row }) => {
                // Check permission first
                if (!canViewSalary) {
                    return (
                        <div className="text-sm flex items-center gap-1">
                            <LockClosedIcon className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-400">Restrito</span>
                        </div>
                    );
                }

                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                // Filtrar apenas salários até hoje (excluir futuros)
                const now = new Date();
                const pastSalaries = salaries?.filter(salary =>
                    new Date(salary.date) <= now
                ) || [];

                if (!pastSalaries.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                const sortedSalaries = [...pastSalaries].sort((a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );

                const lastSalaryDate = new Date(sortedSalaries[0].date);
                const monthsDiff = Math.floor((now.getTime() - lastSalaryDate.getTime()) / (1000 * 60 * 60 * 24 * 30));

                const getColorByMonths = (months: number) => {
                    if (months <= 6) return 'text-green-600';
                    if (months <= 12) return 'text-yellow-600'; 
                    return 'text-red-600';
                };
                
                return (
                    <div className="text-sm">
                        <span className={`font-medium ${getColorByMonths(monthsDiff)}`}>
                            {monthsDiff === 0 ? 'Atual' : `${monthsDiff} meses`}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getMonthsSinceLastRaise = (salaries: any[]) => {
                    if (!salaries?.length) return 0;
                    // Filtrar apenas salários até hoje (excluir futuros)
                    const now = new Date();
                    const pastSalaries = salaries.filter(salary => 
                        new Date(salary.date) <= now
                    );
                    if (!pastSalaries.length) return 0;
                    const sorted = [...pastSalaries].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    const lastDate = new Date(sorted[0].date);
                    return Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
                };
                
                return getMonthsSinceLastRaise(rowA.original.salaryHistorySalaries) - 
                       getMonthsSinceLastRaise(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'salaryProgressionComposite',
            header: 'PROGRESSÃO COMPLETA',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length || salaries.length < 2) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                const firstSalary = sortedSalaries[0].amount || 0;
                const lastSalary = sortedSalaries[sortedSalaries.length - 1].amount || 0;
                
                const progression = lastSalary - firstSalary;
                const isPositive = progression > 0;
                
                // Calcular percentual total
                const totalPercentage = firstSalary > 0 ? ((lastSalary - firstSalary) / firstSalary) * 100 : 0;
                
                return (
                    <div className="text-sm space-y-1">
                        <div className={`font-medium ${isPositive ? 'text-green-600' : progression < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {isPositive ? '↗️' : progression < 0 ? '↘️' : '➡️'} {formatCurrency(Math.abs(progression))}
                        </div>
                        {firstSalary > 0 && (
                            <div className={`text-xs font-medium ${isPositive ? 'text-green-600' : progression < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {isPositive ? '+' : ''}{totalPercentage.toFixed(1)}%
                            </div>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getProgression = (salaries: any[]) => {
                    if (!salaries?.length || salaries.length < 2) return 0;
                    const sorted = [...salaries].sort((a, b) => 
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    return (sorted[sorted.length - 1].amount || 0) - (sorted[0].amount || 0);
                };
                
                return getProgression(rowA.original.salaryHistorySalaries) - 
                       getProgression(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'plannedSalaryIncrease',
            header: 'AUMENTO PLANEJADO',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                // Filtrar salários futuros (data maior que hoje)
                const now = new Date();
                const futureSalaries = salaries.filter(salary => 
                    new Date(salary.date) > now
                ).sort((a, b) => 
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                
                if (!futureSalaries.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const nextSalary = futureSalaries[0];
                const formattedDate = formatDateSafe(nextSalary.date);
                
                return (
                    <div className="text-sm space-y-1">
                        <div className="text-blue-600 font-medium">
                            {formatCurrency(nextSalary.amount)}
                        </div>
                        <div className="text-xs text-gray-500">
                            📅 {formattedDate}
                        </div>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getNextPlannedSalary = (salaries: any[]) => {
                    if (!salaries?.length) return { amount: 0, date: new Date() };
                    const now = new Date();
                    const future = salaries.filter(s => new Date(s.date) > now)
                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                    return future[0] || { amount: 0, date: new Date() };
                };
                
                const nextA = getNextPlannedSalary(rowA.original.salaryHistorySalaries);
                const nextB = getNextPlannedSalary(rowB.original.salaryHistorySalaries);
                
                return nextA.amount - nextB.amount;
            },
        }),
        
        columnHelper.display({
            id: 'lastRaisePercentage',
            header: '% DO ÚLTIMO AUMENTO',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length || salaries.length < 2) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                
                const lastSalary = sortedSalaries[0].amount || 0;
                const previousSalary = sortedSalaries[1].amount || 0;
                
                if (previousSalary === 0) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const percentage = ((lastSalary - previousSalary) / previousSalary) * 100;
                const isPositive = percentage > 0;
                
                return (
                    <div className="text-sm">
                        <span className={`font-medium ${isPositive ? 'text-green-600' : percentage < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {isPositive ? '+' : ''}{percentage.toFixed(1)}%
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getLastRaisePercentage = (salaries: any[]) => {
                    if (!salaries?.length || salaries.length < 2) return 0;
                    const sorted = [...salaries].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    const last = sorted[0].amount || 0;
                    const previous = sorted[1].amount || 0;
                    if (previous === 0) return 0;
                    return ((last - previous) / previous) * 100;
                };
                
                return getLastRaisePercentage(rowA.original.salaryHistorySalaries) - 
                       getLastRaisePercentage(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'totalSalaryEvolution',
            header: '% EVOLUÇÃO SALARIAL',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length || salaries.length < 2) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                const firstSalary = sortedSalaries[0].amount || 0;
                const lastSalary = sortedSalaries[sortedSalaries.length - 1].amount || 0;
                
                if (firstSalary === 0) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const evolution = ((lastSalary - firstSalary) / firstSalary) * 100;
                const isPositive = evolution > 0;
                
                return (
                    <div className="text-sm">
                        <span className={`font-bold ${isPositive ? 'text-green-600' : evolution < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {isPositive ? '+' : ''}{evolution.toFixed(1)}%
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getTotalEvolution = (salaries: any[]) => {
                    if (!salaries?.length || salaries.length < 2) return 0;
                    const sorted = [...salaries].sort((a, b) => 
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    const first = sorted[0].amount || 0;
                    const last = sorted[sorted.length - 1].amount || 0;
                    if (first === 0) return 0;
                    return ((last - first) / first) * 100;
                };
                
                return getTotalEvolution(rowA.original.salaryHistorySalaries) - 
                       getTotalEvolution(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'lastSalaryType',
            header: 'TIPO DO ÚLTIMO SALÁRIO',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];
                
                const getTypeColor = (type: string) => {
                    switch (type) {
                        case 'CLT':
                            return 'text-green-700 bg-green-100';
                        case 'PJ':
                            return 'text-blue-700 bg-blue-100';
                        default:
                            return 'text-gray-700 bg-gray-100';
                    }
                };
                
                return (
                    <div className="text-sm">
                        {lastSalary.salaryType ? (
                            <span className={`font-medium px-2 py-1 rounded text-xs ${getTypeColor(lastSalary.salaryType)}`}>
                                {lastSalary.salaryType}
                            </span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.display({
            id: 'lastExtraAmount',
            header: 'ÚLTIMO EXTRA/CAJU',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];
                
                return (
                    <div className="text-sm">
                        {lastSalary.extras && lastSalary.extras > 0 ? (
                            <span className="text-green-600 font-medium">
                                💰 {formatCurrency(lastSalary.extras)}
                            </span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getLastExtra = (salaries: any[]) => {
                    if (!salaries?.length) return 0;
                    const sorted = [...salaries].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    return sorted[0].extras || 0;
                };
                
                return getLastExtra(rowA.original.salaryHistorySalaries) - 
                       getLastExtra(rowB.original.salaryHistorySalaries);
            },
        }),
        
        columnHelper.display({
            id: 'lastSalaryDate',
            header: 'DATA DO ÚLTIMO SALÁRIO',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];
                
                return (
                    <div className="text-sm">
                        <span className="text-gray-700">
                            {formatDate(lastSalary.date)}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getLastSalaryDate = (salaries: any[]) => {
                    if (!salaries?.length) return new Date(0);
                    const sorted = [...salaries].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    return new Date(sorted[0].date);
                };
                
                return getLastSalaryDate(rowA.original.salaryHistorySalaries).getTime() - 
                       getLastSalaryDate(rowB.original.salaryHistorySalaries).getTime();
            },
        }),
        
        columnHelper.display({
            id: 'lastSalaryReason',
            header: 'MOTIVO DO ÚLTIMO SALÁRIO',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];
                
                return (
                    <div className="text-sm max-w-[200px] truncate">
                        {lastSalary.reason ? (
                            <span className="text-gray-700" title={lastSalary.reason}>
                                {lastSalary.reason}
                            </span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),
        
        columnHelper.display({
            id: 'lastSalaryObservations',
            header: 'OBSERVAÇÕES DO ÚLTIMO SALÁRIO',
            cell: ({ row }) => {
                const salaries = row.original.salaryHistorySalaries;
                if (!salaries?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const sortedSalaries = [...salaries].sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const lastSalary = sortedSalaries[0];
                
                return (
                    <div className="text-sm max-w-[200px] truncate">
                        {lastSalary.observations ? (
                            <span className="text-gray-700" title={lastSalary.observations}>
                                📝 {lastSalary.observations}
                            </span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: sortAccentInsensitive,
            // Não precisa de filterFn - dados já vêm filtrados
        }),

        ...buildVacationColumns(),

        // === LOTE 10 - Formação Acadêmica e Informações de Saúde (Finalizando campos relacionais) ===
        
        columnHelper.display({
            id: 'highestEducationLevel',
            header: 'MAIOR NÍVEL DE FORMAÇÃO',
            cell: ({ row }) => {
                const { educations = [] } = row.original;
                if (!educations?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                // Hierarquia de níveis educacionais
                const educationHierarchy = {
                    'Doutorado': 5,
                    'Mestrado': 4,
                    'Pós-graduação': 3,
                    'Graduação': 2,
                    'Ensino Médio': 1
                };
                
                // Encontrar o maior nível
                let highestEducation = null;
                let highestLevel = 0;
                
                for (const education of educations) {
                    const level = educationHierarchy[education.type as keyof typeof educationHierarchy] || 0;
                    if (level > highestLevel) {
                        highestLevel = level;
                        highestEducation = education;
                    }
                }
                
                if (!highestEducation) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const getIconAndColor = (type: string) => {
                    switch (type) {
                        case 'Doutorado': return { icon: '🎓', color: 'text-purple-600' };
                        case 'Mestrado': return { icon: '🎓', color: 'text-blue-600' };
                        case 'Pós-graduação': return { icon: '📚', color: 'text-green-600' };
                        case 'Graduação': return { icon: '🏛️', color: 'text-indigo-600' };
                        case 'Ensino Médio': return { icon: '📖', color: 'text-gray-600' };
                        default: return { icon: '📋', color: 'text-gray-600' };
                    }
                };
                
                const { icon, color } = getIconAndColor(highestEducation.type);
                
                return (
                    <div className="text-sm">
                        <span className={`font-medium ${color}`}>
                            {icon} {highestEducation.type}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const educationHierarchy = {
                    'Doutorado': 5,
                    'Mestrado': 4,
                    'Pós-graduação': 3,
                    'Graduação': 2,
                    'Ensino Médio': 1
                };
                
                const getHighestLevel = (collaborator: any) => {
                    const { educations = [] } = collaborator;
                    if (!educations?.length) return 0;
                    
                    let highestLevel = 0;
                    for (const education of educations) {
                        const level = educationHierarchy[education.type as keyof typeof educationHierarchy] || 0;
                        if (level > highestLevel) {
                            highestLevel = level;
                        }
                    }
                    return highestLevel;
                };
                
                return getHighestLevel(rowB.original) - getHighestLevel(rowA.original); // Descendente
            },
        }),
        
        columnHelper.display({
            id: 'mainEducationInstitution',
            header: 'INSTITUIÇÃO PRINCIPAL',
            cell: ({ row }) => {
                const { educations = [] } = row.original;
                if (!educations?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                // Hierarquia de níveis educacionais para encontrar a principal
                const educationHierarchy = {
                    'Doutorado': 5,
                    'Mestrado': 4,
                    'Pós-graduação': 3,
                    'Graduação': 2,
                    'Ensino Médio': 1
                };
                
                // Encontrar a educação de maior nível
                let mainEducation = null;
                let highestLevel = 0;
                
                for (const education of educations) {
                    const level = educationHierarchy[education.type as keyof typeof educationHierarchy] || 0;
                    if (level > highestLevel) {
                        highestLevel = level;
                        mainEducation = education;
                    }
                }
                
                if (!mainEducation?.institution) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                return (
                    <div className="text-sm max-w-[200px] truncate">
                        <span className="text-gray-700" title={mainEducation.institution}>
                            🏫 {mainEducation.institution}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getMainInstitution = (collaborator: any) => {
                    const { educations = [] } = collaborator;
                    if (!educations?.length) return '';
                    
                    const educationHierarchy = {
                        'Doutorado': 5,
                        'Mestrado': 4,
                        'Pós-graduação': 3,
                        'Graduação': 2,
                        'Ensino Médio': 1
                    };
                    
                    let mainEducation = null;
                    let highestLevel = 0;
                    
                    for (const education of educations) {
                        const level = educationHierarchy[education.type as keyof typeof educationHierarchy] || 0;
                        if (level > highestLevel) {
                            highestLevel = level;
                            mainEducation = education;
                        }
                    }
                    
                    return mainEducation?.institution || '';
                };
                
                const institutionA = getMainInstitution(rowA.original);
                const institutionB = getMainInstitution(rowB.original);
                return institutionA.localeCompare(institutionB, 'pt-BR', { sensitivity: 'base' });
            },
        }),
        
        columnHelper.display({
            id: 'lastMedicalCertificate',
            header: 'ÚLTIMO ATESTADO MÉDICO',
            cell: ({ row }) => {
                const { medicalCertificates = [] } = row.original;
                if (!medicalCertificates?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                // Encontrar o atestado mais recente
                const sortedCertificates = [...medicalCertificates]
                    .filter(cert => cert.date)
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                
                if (!sortedCertificates.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                const lastCertificate = sortedCertificates[0];
                const certificateDate = new Date(lastCertificate.date);
                const today = new Date();
                const monthsAgo = Math.floor((today.getTime() - certificateDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
                
                const getColorByRecency = (months: number) => {
                    if (months <= 3) return 'text-red-600'; // Muito recente - pode indicar problemas
                    if (months <= 12) return 'text-yellow-600'; // Moderadamente recente
                    return 'text-green-600'; // Antigo - bom sinal
                };
                
                return (
                    <div className="text-sm">
                        <span className={`font-medium ${getColorByRecency(monthsAgo)}`}>
                            🏥 {lastCertificate.days} dias
                        </span>
                        <div className="text-xs text-gray-500">
                            {formatDate(lastCertificate.date)}
                        </div>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getLastCertificateDate = (collaborator: any) => {
                    const { medicalCertificates = [] } = collaborator;
                    if (!medicalCertificates?.length) return new Date(0);
                    
                    const sorted = medicalCertificates
                        .filter((cert: any) => cert.date)
                        .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    
                    return sorted.length ? new Date(sorted[0].date) : new Date(0);
                };
                
                return getLastCertificateDate(rowB.original).getTime() - getLastCertificateDate(rowA.original).getTime(); // Mais recente primeiro
            },
        }),
        
        columnHelper.display({
            id: 'totalMedicalDaysOff',
            header: 'TOTAL DE DIAS AFASTADOS',
            cell: ({ row }) => {
                const { medicalCertificates = [] } = row.original;
                if (!medicalCertificates?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }
                
                // Calcular total de dias afastados
                const totalDays = medicalCertificates.reduce((total: number, cert: any) => {
                    return total + (cert.days || 0);
                }, 0);
                
                if (totalDays === 0) {
                    return <span className="text-gray-400 text-sm">0 dias</span>;
                }
                
                const getColorByTotal = (days: number) => {
                    if (days >= 60) return 'text-red-600'; // Alto número de dias
                    if (days >= 30) return 'text-yellow-600'; // Moderado
                    return 'text-blue-600'; // Baixo
                };
                
                const certificateCount = medicalCertificates.length;
                
                return (
                    <div className="text-sm">
                        <span className={`font-medium ${getColorByTotal(totalDays)}`}>
                            🩺 {totalDays} dias
                        </span>
                        <div className="text-xs text-gray-500">
                            {certificateCount} atestado{certificateCount > 1 ? 's' : ''}
                        </div>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getTotalDays = (collaborator: any) => {
                    const { medicalCertificates = [] } = collaborator;
                    return medicalCertificates.reduce((total: number, cert: any) => {
                        return total + (cert.days || 0);
                    }, 0);
                };
                
                return getTotalDays(rowB.original) - getTotalDays(rowA.original); // Descendente
            },
        }),
        
        // === LOTE 11 - Recursos Técnicos (Finalizando sistema completo) ===
        
        columnHelper.display({
            id: 'primarySkill',
            header: 'HABILIDADES',
            cell: ({ row }) => {
                const resources = (row.original.resources ?? []) as ResolvedResource[];
                if (!resources?.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                const capabilities = buildTechnologyProficiencyEntries(resources);
                if (!capabilities.length) {
                    return <span className="text-gray-400 text-sm">-</span>;
                }

                return (
                    <div className="max-w-[360px]">
                        <div className="flex flex-wrap gap-1.5">
                            {capabilities.map((capability) => (
                                <CapabilityBadge
                                    key={capability.key}
                                    capability={capability}
                                    showPrimary
                                    size="sm"
                                />
                            ))}
                        </div>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const getPrimaryTechnology = (collaborator: any) => {
                    const resources = (collaborator.resources ?? []) as ResolvedResource[];
                    const primary = getPrimaryResource(resources);
                    return resolveTechnologyLabel(primary) ?? '';
                };

                const techA = getPrimaryTechnology(rowA.original);
                const techB = getPrimaryTechnology(rowB.original);
                return techA.localeCompare(techB, 'pt-BR', { sensitivity: 'base' });
            },
        }),
        
        columnHelper.display({
            id: 'actions',
            header: 'AÇÕES',
            cell: ({ row }) => {
                const collaboratorId = row.original.id!;

                const actionItems: ActionMenuItem[] = [
                    {
                        label: 'Editar',
                        onClick: () => handleEditClick(collaboratorId),
                        icon: PencilIcon,
                    },
                    {
                        label: 'Atualização DHO',
                        onClick: () => handleOpenDhoUpdateModal(collaboratorId),
                        icon: UserGroupIcon,
                    },
                    {
                        label: 'Atualização DP',
                        onClick: () => handleOpenDpUpdateModal(collaboratorId),
                        icon: BuildingOfficeIcon,
                    },
                    {
                        label: 'Atualização Tech',
                        onClick: () => handleOpenTechUpdateModal(collaboratorId),
                        icon: CpuChipIcon,
                    },
                    {
                        label: 'Alocações',
                        onClick: () => handleNavigateToAllocations(collaboratorId),
                        icon: ClipboardDocumentListIcon,
                    },
                    {
                        label: 'Excluir e bloquear e-mail',
                        onClick: () => handleRequestDeleteAndBlacklist(row.original),
                        icon: LockClosedIcon,
                        itemClassName: 'text-red-600',
                        iconClassName: 'text-red-500',
                        disabled: !resolvePrimaryEmail(row.original),
                    },
                ];

                return (
                    <div className="flex justify-end">
                        <ActionMenu items={actionItems} />
                    </div>
                );
            },
            enableSorting: false,
            enableHiding: false, // Coluna essencial - não pode ser ocultada
        }),
    ], [sortConsolidatedStatus, sortDhoStatus, sortDpStatus, sortTechStatus, sortUserStatus, handleNavigateToAllocations, handleEditAllocation, handleRequestDeleteAndBlacklist]);


    const table = useReactTable({
        data: displayCollaborators, // Dados já filtrados pelo sistema unificado
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableHiding: true,
        state: {
            pagination,
            sorting,
            columnVisibility,
        },
        onPaginationChange: (updater) =>
            setPagination((prev) => functionalUpdate(updater, prev)),
        onSortingChange: (updater) =>
            setSorting((prev) => functionalUpdate(updater, prev)),
    });

    // Sync pagination & sorting to URL
    useEffect(() => {
        const params = new URLSearchParams(searchParams);

        params.set('page', String(pagination.pageIndex));
        params.set('size', String(pagination.pageSize));
        params.set('sort', sorting[0]?.id ?? 'consolidatedStatus');
        params.set('dir', sorting[0]?.desc ? 'desc' : 'asc');

        if (params.toString() !== searchParams.toString()) {
            setSearchParams(params, { replace: true });
        }
    }, [pagination, sorting, searchParams, setSearchParams]);

    // Os dados já estão filtrados pelo sistema unificado


    return (
        <>
            {/* Table Section */}
            <TanStackTableView
                table={table}
                isLoading={isLoading || capabilityCatalogQuery.isLoading}
                loadingMessage="Carregando colaboradores..."
            />

            {/* Modals */}
            <DhoUpdateModal
                isOpen={isDhoModalOpen}
                onClose={handleCloseDhoUpdateModal}
                collaboratorId={selectedCollaboratorId}
            />

            <DpUpdateModal
                isOpen={isDpModalOpen}
                onClose={handleCloseDpUpdateModal}
                collaboratorId={selectedCollaboratorId}
            />

            <TechUpdateModal
                isOpen={isTechModalOpen}
                onClose={handleCloseTechUpdateModal}
                collaboratorId={selectedCollaboratorId}
            />

            <ConfirmDialog
                isOpen={Boolean(deleteTarget)}
                title="Excluir conta e bloquear e-mail"
                message={`Use esta ação apenas para caixas de e-mail administrativas importadas automaticamente (ex.: financeiro@, suporte@) que não representam um colaborador individual. O registro será removido e o e-mail ficará na blacklist para futuras sincronizações.\n\nE-mail alvo: ${deleteTarget?.email || '—'}`}
                confirmText={isDeleting ? 'Removendo...' : 'Excluir e bloquear'}
                cancelText="Cancelar"
                onConfirm={handleConfirmDeleteAndBlacklist}
                onCancel={() => {
                    if (isDeleting) return;
                    setDeleteTarget(null);
                }}
                type="danger"
            />


        </>
    );
};

export default CollaboratorStatusManagementTableView; 
